import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { siteTexts } from "../i18n/siteTexts";
import "./OrganizerTimeInput.css";

type OrganizerTimeInputProps = {
  label: ReactNode;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
  hideLabel?: boolean;
  className?: string;
};

function parseClockTime(value?: string | null) {
  const raw = String(value || "").trim();
  if (!raw) return null;

  const compactMatch = raw.match(/^(\d{1,2})(\d{2})$/);
  const colonMatch = raw.match(/^(\d{1,2}):(\d{2})$/);
  const match = colonMatch || compactMatch;
  if (!match) return null;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (
    !Number.isFinite(hours) ||
    !Number.isFinite(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return null;
  }

  return { hours, minutes };
}

export function formatClockTime(value?: string | null) {
  const parsed = parseClockTime(value);
  if (!parsed) return "";
  return `${String(parsed.hours).padStart(2, "0")}:${String(
    parsed.minutes,
  ).padStart(2, "0")}`;
}

export function parseClockTimeToDate(value?: string | null) {
  const parsed = parseClockTime(value);
  if (!parsed) return null;
  return new Date(1970, 0, 1, parsed.hours, parsed.minutes, 0, 0);
}

function sanitizeTimeDraft(value: string) {
  return value.replace(/[^\d:]/g, "").slice(0, 5);
}

function buildTimeOptions() {
  const options: string[] = [];
  for (let hours = 0; hours < 24; hours += 1) {
    for (let minutes = 0; minutes < 60; minutes += 15) {
      options.push(
        `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`,
      );
    }
  }
  return options;
}

export default function OrganizerTimeInput({
  label,
  value,
  onChange,
  disabled = false,
  required = false,
  hideLabel = false,
  className = "",
}: OrganizerTimeInputProps) {
  const [draft, setDraft] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [popoverStyle, setPopoverStyle] = useState<CSSProperties>({});
  const listboxId = useId();
  const rootRef = useRef<HTMLLabelElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const timeOptions = useMemo(buildTimeOptions, []);
  const normalizedValue = formatClockTime(value);
  const accessibleLabel = typeof label === "string" ? label : undefined;
  const pickerButtonLabel = accessibleLabel
    ? `${siteTexts.common.timePicker.open}: ${accessibleLabel}`
    : siteTexts.common.timePicker.open;

  useEffect(() => {
    setDraft(value || "");
  }, [value]);

  const updatePosition = useCallback(() => {
    const trigger = rootRef.current;
    const popover = popoverRef.current;
    if (!trigger || !popover) return;

    const margin = 12;
    const gap = 8;
    const triggerRect = trigger.getBoundingClientRect();
    const popoverRect = popover.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const maxLeft = Math.max(margin, viewportWidth - popoverRect.width - margin);
    const maxTop = Math.max(margin, viewportHeight - popoverRect.height - margin);
    const fitsBelow =
      viewportHeight - triggerRect.bottom >= popoverRect.height + gap + margin;
    const preferredTop = fitsBelow
      ? triggerRect.bottom + gap
      : triggerRect.top - popoverRect.height - gap;
    const left = Math.min(Math.max(margin, triggerRect.left), maxLeft);
    const top = Math.min(Math.max(margin, preferredTop), maxTop);

    setPopoverStyle({ left, top, minWidth: triggerRect.width });
  }, []);

  const selectTime = useCallback(
    (time: string) => {
      setDraft(time);
      if (time !== value) onChange(time);
      inputRef.current?.focus();
      setOpen(false);
    },
    [onChange, value],
  );

  const commit = useCallback(
    (raw: string) => {
      const formatted = formatClockTime(raw);
      if (!formatted) {
        setDraft(value || "");
        return;
      }
      setDraft(formatted);
      if (formatted !== value) onChange(formatted);
    },
    [onChange, value],
  );

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      if (
        !rootRef.current?.contains(target) &&
        !popoverRef.current?.contains(target)
      ) {
        setOpen(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return;
    const frameId = window.requestAnimationFrame(updatePosition);
    return () => window.cancelAnimationFrame(frameId);
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const handlePositionChange = () => updatePosition();
    window.addEventListener("resize", handlePositionChange);
    window.addEventListener("scroll", handlePositionChange, true);
    return () => {
      window.removeEventListener("resize", handlePositionChange);
      window.removeEventListener("scroll", handlePositionChange, true);
    };
  }, [open, updatePosition]);

  const popover = (
    <div
      ref={popoverRef}
      className="organizer-time-popover"
      style={popoverStyle}
      id={listboxId}
      role="listbox"
      aria-label={accessibleLabel}
    >
      {timeOptions.map((time) => (
        <button
          key={time}
          type="button"
          className={`organizer-time-option${
            time === normalizedValue ? " is-selected" : ""
          }`}
          onClick={() => selectTime(time)}
          role="option"
          aria-selected={time === normalizedValue}
        >
          {time}
        </button>
      ))}
    </div>
  );

  return (
    <label
      ref={rootRef}
      className={`field organizer-time-input${className ? ` ${className}` : ""}`}
    >
      {!hideLabel ? <span>{label}</span> : null}
      <span className="organizer-time-control">
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          placeholder="HH:MM"
          value={draft}
          onFocus={() => {
            if (!disabled) setOpen(true);
          }}
          onChange={(event) => {
            const next = sanitizeTimeDraft(event.target.value);
            setDraft(next);
            if (/^\d{1,2}:\d{2}$/.test(next) || /^\d{4}$/.test(next)) {
              const formatted = formatClockTime(next);
              if (formatted && formatted !== value) onChange(formatted);
            }
          }}
          onBlur={() => commit(draft)}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown") {
              event.preventDefault();
              setOpen(true);
            }
            if (event.key === "Enter") {
              event.preventDefault();
              commit(draft);
              setOpen(false);
            }
          }}
          disabled={disabled}
          required={required}
          aria-label={accessibleLabel}
          role="combobox"
          aria-autocomplete="none"
          aria-controls={open ? listboxId : undefined}
          aria-haspopup="listbox"
          aria-expanded={open}
        />
        <button
          type="button"
          className="organizer-time-button"
          onClick={(event) => {
            event.preventDefault();
            if (disabled) return;
            inputRef.current?.focus();
            setOpen(true);
          }}
          disabled={disabled}
          aria-label={pickerButtonLabel}
        >
          <span className="material-symbols-outlined" aria-hidden="true">
            schedule
          </span>
        </button>
      </span>
      {open && createPortal(popover, document.body)}
    </label>
  );
}
